import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";
/**
 * The brand has two badge shapes and this is both of them.
 *
 * `tone="neutral"` is `.chip`: a mono uppercase pill on a surface with a
 * `--border-2` edge, optionally carrying a status dot. Every other tone is
 * `.flag`: the smaller sibling, with border and text in one accent over a 10%
 * wash of the same accent. The tint percentages are the brand's own formula,
 * 10% for the background and 55% for the border.
 */
export const badgeVariants = cva([
    "lg:inline-flex lg:items-center lg:gap-2 lg:whitespace-nowrap",
    "lg:rounded-pill lg:border lg:font-label lg:uppercase lg:font-medium",
    "lg:transition-colors lg:duration-150",
], {
    variants: {
        tone: {
            neutral: "lg:border-hairline-2 lg:bg-card lg:text-foreground",
            primary: "lg:border-primary/55 lg:bg-primary/10 lg:text-primary",
            success: "lg:border-success/55 lg:bg-success/10 lg:text-success",
            warning: "lg:border-warning/55 lg:bg-warning/10 lg:text-warning",
            danger: "lg:border-destructive/55 lg:bg-destructive/10 lg:text-destructive",
            info: "lg:border-info/55 lg:bg-info/10 lg:text-info",
            accent: "lg:border-accent/55 lg:bg-accent/10 lg:text-accent",
            /** No border, no wash. For a label that sits inside a busy row. */
            quiet: "lg:border-transparent lg:text-subtle",
        },
        size: {
            sm: "lg:px-2.5 lg:py-1 lg:text-[10px] lg:tracking-label",
            md: "lg:px-4 lg:py-1.5 lg:text-eyebrow lg:tracking-[0.09em]",
        },
    },
    defaultVariants: { tone: "neutral", size: "sm" },
});
export function Badge({ className, tone, size, ...props }) {
    return (_jsx("span", { "data-slot": "badge", className: cn(badgeVariants({ tone, size }), className), ...props }));
}
/**
 * The 6px status dot from `.chip .dot`. It is decorative, so it is hidden from
 * assistive technology: the badge's own text has to say what the state is.
 */
export function BadgeDot({ className, ...props }) {
    return (_jsx("span", { "data-slot": "badge-dot", "aria-hidden": "true", className: cn("lg:size-1.5 lg:shrink-0 lg:rounded-full lg:bg-current", className), ...props }));
}
//# sourceMappingURL=badge.js.map